import { Client } from 'src/api/Client';
import { AxiosRequestConfig } from 'axios';
import {
  PaginatedResponse,
  CreateScriptRequest,
  UpdateScriptRequest,
  ScriptResponse,
  ScriptQueryParams,
  ScriptStatus,
} from './ScriptClient.types';

class ScriptClient extends Client {
  private readonly Route = {
    SCRIPTS: `${this.http.baseUrl}/scripts`,
    SCRIPT: `${this.http.baseUrl}/scripts/{id}`,
    SCRIPT_STATUS: `${this.http.baseUrl}/scripts/{id}/status`,
  };

  /**
   * Create a new script
   * @param data Script creation data with title and body
   * @returns The created script
   */
  public create(data: CreateScriptRequest): Promise<ScriptResponse> {
    return this.http.post(this.Route.SCRIPTS, data);
  }

  /**
   * Update an existing script
   * @param id Script ID
   * @param data Updated script data with title and body
   * @returns The updated script
   */
  public update(id: string, data: UpdateScriptRequest): Promise<ScriptResponse> {
    return this.http.put(this.buildUrl(this.Route.SCRIPT, { id }), data);
  }

  /**
   * Get script by ID
   * @param id Script ID
   * @returns The script with the specified ID
   */
  public get(id: string): Promise<ScriptResponse> {
    return this.http.get(this.buildUrl(this.Route.SCRIPT, { id }));
  }

  /**
   * Get paginated list of scripts with optional filtering and sorting
   * @param params Query parameters for pagination, sorting, and filtering
   * @returns Paginated list of scripts
   */
  public getAll(
    params?: ScriptQueryParams,
    config?: AxiosRequestConfig,
  ): Promise<PaginatedResponse<ScriptResponse>> {
    return this.http.get(this.Route.SCRIPTS, {
      ...config,
      params,
    });
  }

  /**
   * Update the status of a script
   * @param id Script ID
   * @param status New status value
   * @returns The updated script
   */
  public updateStatus(id: string, status: ScriptStatus): Promise<ScriptResponse> {
    return this.http.patch(this.buildUrl(this.Route.SCRIPT_STATUS, { id }), { status });
  }

  /**
   * Delete a script by ID
   * @param id Script ID to delete
   * @returns Success response
   */
  public delete(id: string): Promise<ScriptResponse> {
    return this.http.delete(this.buildUrl(this.Route.SCRIPT, { id }));
  }
}

export default ScriptClient;
